import { useMemo, useState } from 'react';

import {
  Alert,
  AlertIcon,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Select,
  Spinner,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { Campaign, CampaignNominee, getError, useToast } from '@aslanpay/portal-common-lib';

import useCampaignDecision from 'app/hooks/useCampaignDecision';
import useDebounce from 'app/hooks/useDebounce';
import useFetchRecognitionValues from 'app/hooks/useFetchRecognitionValues';
import useFetchWinnerCandidates from 'app/hooks/useFetchWinnerCandidates';

const MAX_MESSAGE = 2000;

/** The refusal when the person picked is not somebody the campaign would accept as its winner. */
const OUTSIDE_THE_CAMPAIGN = 'CMP-20042';

interface RecordDecisionModalProps {
  isOpen: boolean;
  onClose: () => void;
  pride: Campaign;
  prideName: string;
  nominees: CampaignNominee[];
  companyId: string;
}

/**
 * Who won a pride's campaign, or that nobody did.
 * <p>
 * Final on confirmation: picking a winner holds the award against the campaign's pot, so the dialog says
 * what is about to become irreversible rather than leaving it to be discovered.
 */
function RecordDecisionModal({
  isOpen,
  onClose,
  pride,
  prideName,
  nominees,
  companyId,
}: RecordDecisionModalProps) {
  const toast = useToast();
  const queryClient = useQueryClient();

  // A campaign that allows a winner nobody nominated has to offer somebody nobody nominated. Listing only
  // nominees left a pride with none unable to record a decision at all.
  const allowsAnyone = pride.allowWinnerWithoutNominations;
  const prideOnly = allowsAnyone && pride.winnerEligibility === 'ALL_GROUP';

  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const onSearch = useDebounce((value: string) => setQuery(value), 300);

  // Asked of the campaign rather than of the employee list: the campaign knows who it will accept, and a
  // name it does not return is one the announcement would refuse.
  const {
    data: offered,
    isLoading: isLoadingCandidates,
    isError: candidatesFailed,
  } = useFetchWinnerCandidates(pride.id, { query: query.trim() || undefined }, isOpen);

  const candidates = useMemo(() => {
    const countsByIndividualId = new Map(
      nominees.map((nominee) => [nominee.nomineeId, nominee.nominationCount])
    );

    const labelled = (offered?.list ?? []).map((candidate) => {
      const count = countsByIndividualId.get(candidate.individualId);
      const name = `${candidate.firstName} ${candidate.lastName}`.trim();

      return {
        id: candidate.individualId,
        nominated: count !== undefined,
        label:
          count === undefined
            ? name
            : `${name} · ${count} ${count === 1 ? 'nomination' : 'nominations'}`,
      };
    });

    // Those nominated first, as they were before, and the campaign's own order within each.
    return [
      ...labelled.filter((candidate) => candidate.nominated),
      ...labelled.filter((candidate) => !candidate.nominated),
    ];
  }, [nominees, offered]);

  const moreThanListed = (offered?.total ?? 0) > candidates.length;

  const [outcome, setOutcome] = useState<'WINNER' | 'NO_WINNER'>('WINNER');
  const [winnerId, setWinnerId] = useState('');
  const [valueId, setValueId] = useState('');
  const [message, setMessage] = useState('');

  const { data: values } = useFetchRecognitionValues({ companyId, isEnabled: true }, isOpen);

  const { mutate: decide, isLoading } = useCampaignDecision.useRecordDecision({
    onSuccess: () => {
      toast({
        position: 'top-right',
        status: 'success',
        title: outcome === 'WINNER' ? 'Winner recorded' : 'Recorded with no winner',
        description:
          outcome === 'WINNER'
            ? `${prideName}'s award is held for them until it is announced.`
            : `${prideName} closes with nobody winning.`,
      });
      queryClient.invalidateQueries({ queryKey: ['fetchCampaigns'] });
      onClose();
    },
    onError: (error) => {
      const refusal = getError(error);

      // The campaign refuses anyone outside what it admits. The code says that much; which rule it was
      // is something only the portal knows, so it says it rather than leaving the admin guessing.
      const description =
        refusal.code === OUTSIDE_THE_CAMPAIGN && prideOnly
          ? `${prideName} only accepts a winner from its own pride. Nothing was saved.`
          : refusal.message || 'Nothing was saved. Please try again.';

      toast({
        position: 'top-right',
        status: 'error',
        title: 'Could not record the decision',
        description,
      });
    },
  });

  const needsValue = pride.valuesRequired;

  // A search can narrow the list past whoever was picked. The pick is only real while it is on the list,
  // so the dialog never saves a winner it is no longer showing.
  const winnerStillOffered = candidates.some((candidate) => candidate.id === winnerId);
  const canSave =
    !isLoading &&
    (outcome === 'NO_WINNER' ||
      (winnerStillOffered && message.trim().length > 0 && (!needsValue || !!valueId)));

  const onSave = () =>
    decide({
      campaignId: pride.id,
      request:
        outcome === 'NO_WINNER'
          ? { outcome: 'NO_WINNER' }
          : {
              outcome: 'WINNER',
              winnerId,
              valueId: valueId || undefined,
              winnerMessage: message.trim(),
            },
    });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent data-testid="record-decision-modal">
        <ModalHeader>Who won {prideName}?</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex flexDir="column" gap="16px">
            <RadioGroup
              value={outcome}
              onChange={(value) => setOutcome(value as 'WINNER' | 'NO_WINNER')}
            >
              <Stack gap="10px">
                <Radio value="WINNER" data-testid="outcome-winner">
                  Somebody won
                </Radio>
                <Radio value="NO_WINNER" data-testid="outcome-no-winner">
                  Nobody won
                </Radio>
              </Stack>
            </RadioGroup>

            {outcome === 'WINNER' && (
              <>
                <FormControl>
                  <FormLabel>Winner</FormLabel>

                  {allowsAnyone && (
                    <Input
                      mb="8px"
                      value={search}
                      placeholder="Search by name"
                      aria-label="Search the people this campaign will accept"
                      onChange={(event) => {
                        setSearch(event.target.value);
                        onSearch(event.target.value);
                      }}
                      data-testid="winner-search"
                    />
                  )}

                  {candidatesFailed && (
                    <Alert status="error" data-testid="winner-candidates-error">
                      <AlertIcon />
                      The people this campaign will accept could not be read. Close and try again.
                    </Alert>
                  )}

                  {!candidatesFailed && isLoadingCandidates && (
                    <Flex align="center" gap="8px" data-testid="winner-candidates-loading">
                      <Spinner size="sm" />
                      <Text textStyle="small">Reading who this campaign will accept…</Text>
                    </Flex>
                  )}

                  {!candidatesFailed && !isLoadingCandidates && candidates.length === 0 && (
                    <Text textStyle="small" data-testid="winner-candidates-empty">
                      {query.trim()
                        ? 'Nobody this campaign will accept matches that name.'
                        : 'This campaign has nobody it will accept as a winner.'}
                    </Text>
                  )}

                  {!candidatesFailed && !isLoadingCandidates && candidates.length > 0 && (
                    <Select
                      placeholder="Choose the winner"
                      value={winnerStillOffered ? winnerId : ''}
                      onChange={(event) => setWinnerId(event.target.value)}
                      data-testid="winner-select"
                    >
                      {candidates.map((candidate) => (
                        <option key={candidate.id} value={candidate.id}>
                          {candidate.label}
                        </option>
                      ))}
                    </Select>
                  )}

                  <FormHelperText>
                    {!allowsAnyone && 'Only somebody who was nominated can win this campaign.'}
                    {allowsAnyone &&
                      prideOnly &&
                      'This campaign allows a winner nobody nominated, from its own pride. Those nominated come first.'}
                    {allowsAnyone &&
                      !prideOnly &&
                      'This campaign allows a winner nobody nominated, from anywhere in the company. Those nominated come first.'}
                    {moreThanListed &&
                      ` Showing ${candidates.length.toLocaleString('en-GB')} of ${(
                        offered?.total ?? 0
                      ).toLocaleString('en-GB')}; search to narrow.`}
                  </FormHelperText>
                </FormControl>

                <FormControl>
                  <FormLabel>{needsValue ? 'Value' : 'Value (optional)'}</FormLabel>
                  <Select
                    placeholder="Choose a value"
                    value={valueId}
                    onChange={(event) => setValueId(event.target.value)}
                    data-testid="value-select"
                  >
                    {(values?.list ?? []).map((value) => (
                      <option key={value.id} value={value.id}>
                        {value.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Published with the win</FormLabel>
                  <Textarea
                    value={message}
                    maxLength={MAX_MESSAGE}
                    onChange={(event) => setMessage(event.target.value)}
                    placeholder="What the winner is being recognised for"
                    data-testid="winner-message-input"
                  />
                  <FormHelperText>
                    Everyone in the company reads this. {MAX_MESSAGE - message.length} characters
                    left.
                  </FormHelperText>
                </FormControl>
              </>
            )}

            <Text textStyle="extraSmallRegular" color="brand.darkGray">
              Who won is final once recorded. The value and these words can still be changed until
              the announcement publishes.
            </Text>
          </Flex>
        </ModalBody>
        <ModalFooter gap="12px">
          <Button variant="outline" onClick={onClose} data-testid="cancel-decision">
            Cancel
          </Button>
          <Button
            onClick={onSave}
            isDisabled={!canSave}
            isLoading={isLoading}
            data-testid="confirm-decision"
          >
            Record decision
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default RecordDecisionModal;
